import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMediaTypeFromExtension, getExtensionFromName } from "@lib/mime/mime";
import { ASSET_TYPES, INGEST_TYPE, LIMIT_CHAR, MEDIA_TYPES, UPLOAD_FILE_LOCATION_TYPE } from "@constants";
import {
    getLanguages,
    updateElementDetail,
    setValidationError,
    updateState,
    updateElements,
    resetElementsValidationError,
} from "@modules/createAsset";
import validateInput from "@cores/validateInput";
import UploadAssetItemMulti from "@components_v2/upload-asset-item/upload-asset-item-multi";
import UploadAssetItemIndividual from "@components_v2/upload-asset-item/upload-asset-item-individual";

const UploadAssetItem = ({ disabled = false, isOneAsset }) => {
    const dispatch = useDispatch();

    const createAsset = useSelector(({ createAsset }) => createAsset);

    const onChangeLabel = (key, index, value) => {
        dispatch(
            updateElementDetail(
                key,
                {
                    label: value,
                },
                index,
            ),
        );

        dispatch(
            setValidationError({
                key: `elements-${key}-label`,
                error: validateInput(value, {
                    required: true,
                    maxLength: LIMIT_CHAR.name,
                }),
            }),
        );
    };

    const onChangeCaptionKind = (key, index, value) => {
        dispatch(
            updateElementDetail(
                key,
                {
                    kind: value,
                },
                index,
            ),
        );

        let newElementsByMediaType = [];
        newElementsByMediaType = createAsset.elementsByMediaType.map((el) =>
            el.key === key ? { ...el, type: value } : el,
        );
        // if (!index) {
        //
        // } else {
        //     newElementsByMediaType = createAsset.elementsByMediaType.map((el) => ({ ...el, type: value }));
        // }

        dispatch(
            updateState({
                elementsByMediaType: newElementsByMediaType,
            }),
        );

        dispatch(
            setValidationError({
                key: `elements-${key}-kind`,
                error: validateInput(value, {
                    required: true,
                }),
            }),
        );
    };
    const onChangeFileName = (key, index, value) => {
        const selectedFiles = createAsset.selectedFiles;
        if (!index) selectedFiles[key][0].displayName = value;
        else selectedFiles[key][index].displayName = value;

        dispatch(
            updateState({
                elements: createAsset.elements,
                selectedFiles,
                elementsByMediaType: createAsset.elementsByMediaType.map((el) =>
                    el.key === key ? { ...el, name: value } : el,
                ),
            }),
        );

        dispatch(
            setValidationError({
                key: `elements-${key}-name`,
                error: validateInput(value, {
                    required: true,
                }),
            }),
        );
    };

    const onChangeLanguage = (key, index, codes) => {
        dispatch(
            updateElementDetail(
                key,
                {
                    language: codes[0],
                    country: codes[1],
                },
                index,
            ),
        );

        dispatch(
            setValidationError({
                key: `elements-${key}-language`,
                error: validateInput(codes, {
                    required: true,
                }),
            }),
        );
    };

    const setInitValidationErrors = (key, existingElement) => {
        if (!existingElement?.kind) {
            dispatch(
                setValidationError({
                    key: `elements-${key}-kind`,
                    error: { type: "REQUIRED" },
                }),
            );
        }
        if (!existingElement?.label) {
            dispatch(
                setValidationError({
                    key: `elements-${key}-label`,
                    error: { type: "REQUIRED" },
                }),
            );
        }
        if (!existingElement?.language) {
            dispatch(
                setValidationError({
                    key: `elements-${key}-language`,
                    error: { type: "REQUIRED" },
                }),
            );
        }
    };

    const converToMediaType = (file) => {
        const mediaType = getMediaTypeFromExtension(getExtensionFromName(file.name));

        if (mediaType === MEDIA_TYPES.CAPTION) {
            return {
                ingestType:
                    file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL ? INGEST_TYPE.DIRECT : INGEST_TYPE.REMOTE,
                mediaType: mediaType,
                type: getAssetType(mediaType),
            };
        }
        if ([MEDIA_TYPES.VIDEO, MEDIA_TYPES.AUDIO].includes(mediaType)) {
            return {
                ingestType:
                    file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL ? INGEST_TYPE.DIRECT : INGEST_TYPE.REMOTE,
                mediaType: mediaType,
                type: getAssetType(mediaType),
                ...convertToAssetInfo(file),
            };
        }

        if ([MEDIA_TYPES.IMAGE, MEDIA_TYPES.FILE].includes(mediaType)) {
            return {
                ingestType:
                    file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL ? INGEST_TYPE.DIRECT : INGEST_TYPE.REMOTE,
                mediaType: mediaType,
                type: getAssetType(mediaType),
            };
        }
    };
    const convertToElements = (file) => {
        const key = file.key;
        const mediaType = getMediaTypeFromExtension(getExtensionFromName(file.name));

        if (mediaType === MEDIA_TYPES.CAPTION) {
            return {
                key: key,
                url: file.url,
                size: file.size,
                mimeType: "text/vtt",
                locationType: file.locationType,
            };
        }
        if ([MEDIA_TYPES.VIDEO, MEDIA_TYPES.AUDIO].includes(mediaType)) {
            return {
                key: key,
                size: file.size,
                mimeType: file.type,
                urls: [file.url],
                locationType: file.locationType,
            };
        }

        if ([MEDIA_TYPES.IMAGE, MEDIA_TYPES.FILE].includes(mediaType)) {
            return {
                key: key,
                width: 0,
                height: 0,
                url: file.url,
                isPreferred: false,
                size: file.size,
                mimeType: file.type,
                locationType: file.locationType,
            };
        }
    };

    const getAssetType = (mediaType) => {
        if (mediaType === MEDIA_TYPES.IMAGE) return ASSET_TYPES.IMAGE;
        else if (mediaType === MEDIA_TYPES.TEXT) return ASSET_TYPES.CAPTION;
        else if (mediaType === MEDIA_TYPES.CAPTION) return ASSET_TYPES.CAPTION;
        else if (mediaType === MEDIA_TYPES.FILE) return ASSET_TYPES.FILE;
        else return "";
    };

    const convertToAssetInfo = (file) => {
        const fileName = file.name;
        const extension = fileName.slice(fileName.lastIndexOf(".") + 1).toUpperCase();
        return { type: extension };
    };
    // const saveElementDetail = (files) => {
    //     files?.map((file, index) => {
    //         const mediaType = getMediaTypeFromExtension(getExtensionFromName(file.displayName));
    //
    //         if ([MEDIA_TYPES.CAPTION, MEDIA_TYPES.TEXT].includes(mediaType)) {
    //             dispatch(
    //                 updateElementDetail(index, {
    //                     url: file.url,
    //                     size: file.size,
    //                     mimeType: "text/vtt",
    //                     locationType: file.locationType,
    //                     ingestType:
    //                         file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL
    //                             ? INGEST_TYPE.DIRECT
    //                             : INGEST_TYPE.REMOTE,
    //                     mediaType: mediaType,
    //                     type: getAssetType(mediaType),
    //                 }),
    //             );
    //
    //             dispatch(
    //                 setValidationError({
    //                     key: `elements-${file.key}-url`,
    //                 }),
    //             );
    //         }
    //     });
    // };

    const removeAll = useCallback(
        (key) => {
            const filesToRemove = createAsset.originFiles.filter((file) =>
                createAsset.elements[key]?.some((element) => element.key === file.key),
            );

            filesToRemove.forEach((file) => {
                if (file.accessUrl && file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) {
                    URL.revokeObjectURL(file.accessUrl);
                }
            });

            const newSelectedFiles = createAsset.selectedFiles;
            const newElements = createAsset.elements;
            const newOriginFiles = createAsset.originFiles.filter(
                (v) => newElements[key].filter((e) => e.key === v.key).length === 0,
            );

            delete newSelectedFiles[key];
            delete newElements[key];

            dispatch(
                updateState({
                    selectedFiles: newSelectedFiles,
                    data: {
                        ...createAsset.data,
                    },
                    elements: newElements,
                    originFiles: newOriginFiles,
                }),
            );
        },
        [createAsset, dispatch],
    );

    const removeSelectedFile = useCallback(
        (key, index) => {
            if (createAsset.selectedFiles[key].length === 1) {
                removeAll(key);
                return;
            }

            // 삭제되는 파일의 Blob URL 먼저 revoke
            const fileToRemove = createAsset.originFiles.find(
                (file) => file.key === createAsset.elements[key][index].key,
            );

            if (fileToRemove?.accessUrl && fileToRemove.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) {
                URL.revokeObjectURL(fileToRemove.accessUrl);
            }

            const selectedFiles = createAsset.selectedFiles[key]?.filter((v, i) => i !== index);
            const elements = createAsset.elements[key]?.filter((v, i) => i !== index);
            const originFiles = createAsset.originFiles.filter((v) => v.key !== createAsset.elements[key][index].key);

            dispatch(
                updateState({
                    selectedFiles: {
                        ...createAsset.selectedFiles,
                        [key]: selectedFiles,
                    },
                    elements: {
                        ...createAsset.elements,
                        [key]: elements,
                    },
                    originFiles,
                }),
            );
        },
        [createAsset, dispatch, removeAll],
    );

    const updateSelectedFiles = useCallback(() => {
        const files = createAsset.originFiles;
        if (!files || (files && files.length === 0)) return;
        if (createAsset?.languages?.data?.length === 0) dispatch(getLanguages());

        let newElementsByMediaType = [];
        let elementsObj = new Object();
        let filesObj = new Object();

        //기존 수정된 부분을 그대로 유지
        files?.map((file) => {
            const newElements = convertToElements(file);
            const mediaType = converToMediaType(file);

            const isGroupItem = ["IMAGE", "CAPTION"].includes(mediaType.type);
            const isExist = newElementsByMediaType.find((o) => o.type === mediaType.type) !== undefined;

            if (!isOneAsset || (isOneAsset && (!isGroupItem || (isGroupItem && !isExist))))
                newElementsByMediaType.push({
                    key: isOneAsset && isGroupItem ? mediaType.type : file.key,
                    name: `${createAsset.data?.name ?? ""}${file.name}`,
                    ...mediaType,
                });

            const elKey = !isOneAsset ? mediaType.type : file.key;
            const el = createAsset.elements[elKey]?.find((f) => f.key === file.key) ?? newElements;
            const newEl = el
                ? { ...el, key: file.key, name: `${createAsset.data?.name ?? ""}${file.name}` }
                : newElements;

            if (mediaType.type === "CAPTION") {
                // 번들→개별 전환 시, kind가 없는 요소에 같은 그룹의 kind를 적용
                if (!isOneAsset && !newEl.kind) {
                    const siblingKind = createAsset.elements[elKey]?.find((f) => f.kind)?.kind;
                    if (siblingKind) {
                        newEl.kind = siblingKind;
                    }
                }
                const targetKey = isOneAsset ? "CAPTION" : file.key;
                setInitValidationErrors(targetKey, el !== newElements ? newEl : undefined);
            }

            if (!isOneAsset) {
                filesObj[file.key] = [file];
                elementsObj[file.key] = [newEl];
            } else {
                const groupItem = newElementsByMediaType?.find((el) =>
                    isGroupItem ? el.type === mediaType.type : el.key === file.key,
                );

                filesObj[groupItem.key] = filesObj[groupItem.key] ? [...filesObj[groupItem.key], file] : [file];
                elementsObj[groupItem.key] = elementsObj[groupItem.key]
                    ? [...elementsObj[groupItem.key], newEl]
                    : [newEl];
            }
        });

        dispatch(
            updateState({
                elementsByMediaType: newElementsByMediaType,
                selectedFiles: { ...filesObj },
            }),
        );

        dispatch(
            updateElements({
                elements: { ...elementsObj },
            }),
        );
    }, [isOneAsset, createAsset]);

    useEffect(() => {
        updateSelectedFiles();
    }, [createAsset.originFiles]);

    useEffect(async () => {
        dispatch(resetElementsValidationError());

        await updateSelectedFiles();
    }, [isOneAsset]);

    if (isOneAsset)
        return (
            <UploadAssetItemMulti
                files={createAsset.selectedFiles}
                elements={createAsset.elements}
                validationErrors={createAsset.validationErrors}
                removeSelectedFile={removeSelectedFile}
                removeAll={removeAll}
                disabled={disabled}
                onChangeFileName={onChangeFileName}
                onChangeLabel={onChangeLabel}
                onChangeLanguage={onChangeLanguage}
                onChangeCaptionKind={onChangeCaptionKind}
                languages={createAsset.languages.data}
            />
        );
    else
        return (
            <UploadAssetItemIndividual
                files={createAsset.selectedFiles}
                elements={createAsset.elements}
                validationErrors={createAsset.validationErrors}
                removeSelectedFile={removeSelectedFile}
                disabled={disabled}
                onChangeFileName={onChangeFileName}
                onChangeLabel={onChangeLabel}
                onChangeLanguage={onChangeLanguage}
                onChangeCaptionKind={onChangeCaptionKind}
                languages={createAsset.languages.data}
            />
        );
};

export default UploadAssetItem;
